import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import * as THREE from 'three';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './landing.css';

gsap.registerPlugin(ScrollTrigger);

const EMPTY_FORM = { from_name: '', phone: '', from_email: '', level: '', course: '', message: '', website: '' };

const MANIFESTO = [
    ...'Chúng tôi tin một nền tảng tiếng Anh tốt không đến từ sự vội vàng. Nó được đặt từng viên — buổi học nhỏ, cố vấn sát sao, mục tiêu rõ ràng — cho đến khi bạn tìm thấy'
        .split(' ').map(t => ({ t, accent: false })),
    ...'viên đá của riêng mình.'.split(' ').map(t => ({ t, accent: true })),
];

const STEPS = [
    { no: '01', title: 'Đặt móng', body: 'Kiểm tra đầu vào miễn phí. Chúng tôi đo đúng trình độ hiện tại và vẽ lộ trình riêng cho bạn — trước khi đặt viên gạch đầu tiên.' },
    { no: '02', title: 'Xây từng hàng', body: 'Lớp tối đa 8 học viên. Mỗi buổi học là một hàng gạch — chắc rồi mới xây tiếp, không nhồi nhét cho kịp giáo trình.' },
    { no: '03', title: 'Soi từng mạch vữa', body: 'Cố vấn học tập riêng theo dõi điểm số và chuyên cần hàng tuần, điều chỉnh ngay khi bạn chững lại.' },
    { no: '04', title: 'Đặt đá đỉnh', body: 'Cam kết đầu ra rõ ràng. Không đạt mục tiêu của khóa? Bạn được học lại miễn phí — đến khi viên đá đỉnh nằm đúng chỗ.', last: true },
];

const NUMBERS = [
    { to: 1200, unit: '+', label: 'học viên đã và đang theo học' },
    { to: 8, unit: '/lớp', label: 'sĩ số tối đa, giữ chất lượng' },
    { to: 94, unit: '%', label: 'đạt mục tiêu đầu ra của khóa' },
    { to: 7, unit: 'năm', label: 'xây nền tiếng Anh tại Hạ Long' },
];

const ADDRESS = 'Lô A-28+29 KĐT Monbay, đường Hải Long, phường Hạ Long, Quảng Ninh';
const PHONE = '0888 070 619';
const EMAIL = 'Themasonhalong@gmail.com';

const MarqueeUnit = () => (
    <>
        <span className="mq-en">Found Your Stone</span><span className="mq-gem" />
        <span className="mq-vn">Vững từng viên gạch</span><span className="mq-gem sky" />
        <span className="mq-en">English, built to last</span><span className="mq-gem mint" />
        <span className="mq-vn">Hạ Long · từ 2019</span><span className="mq-gem" />
    </>
);

/* Builds the three.js brick wall in the hero canvas. Returns a cleanup function. */
function buildWall(canvas, heroEl, rootEl, reduceMotion) {
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0d1e40);
    scene.fog = new THREE.FogExp2(0x0d1e40, 0.04);

    const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
    camera.position.set(0, 2.4, 14);

    scene.add(new THREE.AmbientLight(0x3a5080, 0.55));
    const keyLight = new THREE.DirectionalLight(0xeaf1fb, 0.85);
    keyLight.position.set(-6, 10, 8);
    scene.add(keyLight);
    const coralLight = new THREE.PointLight(0xe8937d, 0, 18, 2);
    coralLight.position.set(0, 5.2, 3);
    scene.add(coralLight);
    const skyLight = new THREE.PointLight(0x52b6ce, 0.7, 26, 2);
    skyLight.position.set(9, 3, 6);
    scene.add(skyLight);

    const groundGeo = new THREE.PlaneGeometry(220, 220);
    const groundMat = new THREE.MeshStandardMaterial({ color: 0x0a1734, roughness: 1 });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.55;
    scene.add(ground);

    const dustGeo = new THREE.BufferGeometry();
    const dustCount = 160;
    const dustPos = new Float32Array(dustCount * 3);
    for (let i = 0; i < dustCount; i++) {
        dustPos[i * 3] = (Math.random() - 0.5) * 34;
        dustPos[i * 3 + 1] = Math.random() * 11;
        dustPos[i * 3 + 2] = (Math.random() - 0.5) * 22;
    }
    dustGeo.setAttribute('position', new THREE.BufferAttribute(dustPos, 3));
    const dustMat = new THREE.PointsMaterial({ color: 0x52b6ce, size: 0.05, transparent: true, opacity: 0.45, depthWrite: false });
    const dust = new THREE.Points(dustGeo, dustMat);
    scene.add(dust);

    const wall = new THREE.Group();
    scene.add(wall);

    const ROWS = 7, COLS = 7;
    const BL = 1.9, BH = 0.82, BD = 0.9, GAP = 0.14;
    const navyShades = [0x1c3b6e, 0x16315c, 0x3a5080, 0x24406f, 0x122a52];
    const brickGeo = new THREE.BoxGeometry(BL, BH, BD);
    const bricks = [];
    let keystone = null;

    /* deterministic pseudo-random so the wall looks the same every load */
    let s = 42;
    const rand = () => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };

    const accentSlots = new Set([10, 24, 31]);
    for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
            const i = r * COLS + c;
            const isKeystone = (r === ROWS - 1 && c === Math.floor(COLS / 2));
            let color = navyShades[Math.floor(rand() * navyShades.length)];
            let emissive = 0x000000, emissiveIntensity = 0;
            if (accentSlots.has(i)) {
                color = (i % 2 === 0) ? 0x52b6ce : 0x68be7a;
                emissive = color; emissiveIntensity = 0.18;
            }
            if (isKeystone) { color = 0xe8937d; emissive = 0xe8937d; emissiveIntensity = 0.55; }
            const mat = new THREE.MeshStandardMaterial({
                color, emissive, emissiveIntensity,
                roughness: 0.55 + rand() * 0.3, metalness: 0.05,
            });
            const m = new THREE.Mesh(brickGeo, mat);
            const offset = (r % 2 === 1) ? (BL + GAP) / 2 : 0;
            const tx = (c - (COLS - 1) / 2) * (BL + GAP) + offset;
            const ty = r * (BH + GAP) + BH / 2 - 0.5;
            const tz = (rand() - 0.5) * 0.06 + (isKeystone ? 0.3 : 0);
            m.userData.target = { x: tx, y: ty, z: tz };
            if (reduceMotion) {
                m.position.set(tx, ty, tz);
            } else {
                m.position.set((rand() - 0.5) * 26, 7 + rand() * 9, (rand() - 0.5) * 18 - 4);
                m.rotation.set(rand() * Math.PI, rand() * Math.PI, rand() * Math.PI);
            }
            m.userData.row = r; m.userData.col = c;
            if (isKeystone) keystone = m; else bricks.push(m);
            wall.add(m);
        }
    }
    wall.position.set(0, -0.1, 0);
    wall.rotation.y = -0.10;

    const mouse = { x: 0, y: 0 };
    const onPointerMove = (e) => {
        mouse.x = (e.clientX / window.innerWidth - 0.5) * 2;
        mouse.y = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener('pointermove', onPointerMove);

    const resize = () => {
        const w = canvas.clientWidth, h = canvas.clientHeight;
        renderer.setSize(w, h, false);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', resize);
    resize();

    let heroVisible = true;
    const visObs = new IntersectionObserver(([e]) => { heroVisible = e.isIntersecting; }, { threshold: 0 });
    visObs.observe(heroEl);

    const clock = new THREE.Clock();
    let rafId;
    const tick = () => {
        rafId = requestAnimationFrame(tick);
        if (!heroVisible) return;
        const t = clock.getElapsedTime();
        wall.rotation.y = -0.10 + mouse.x * 0.05;
        wall.rotation.x = mouse.y * 0.022;
        camera.position.y = 2.4 + Math.sin(t * 0.5) * 0.08;
        dust.rotation.y = t * 0.012;
        if (keystone) keystone.material.emissiveIntensity = 0.55 + Math.sin(t * 1.6) * 0.14;
        renderer.render(scene, camera);
    };
    tick();

    /* intro choreography — wall builds row by row, keystone lands last */
    const q = (sel) => rootEl.querySelectorAll(sel);
    let intro = null;
    if (reduceMotion) {
        gsap.set(q('.nav, .eyebrow, .hero-sub, .hero-cta, .scroll-cue, .hero-h1 .w'), { opacity: 1, y: 0 });
        coralLight.intensity = 1.6;
    } else {
        intro = gsap.timeline({ defaults: { ease: 'power3.out' } });
        bricks.forEach(b => {
            const d = 0.15 + b.userData.row * 0.22 + b.userData.col * 0.045;
            intro.to(b.position, { ...b.userData.target, duration: 1.1, ease: 'power3.inOut' }, d);
            intro.to(b.rotation, { x: 0, y: 0, z: 0, duration: 1.1, ease: 'power3.inOut' }, d);
        });
        if (keystone) {
            intro.to(keystone.position, { ...keystone.userData.target, duration: 0.9, ease: 'back.out(1.8)' }, 2.05);
            intro.to(keystone.rotation, { x: 0, y: 0, z: 0, duration: 0.9, ease: 'power3.inOut' }, 2.05);
            intro.to(coralLight, { intensity: 2.6, duration: 0.35, ease: 'power2.in' }, 2.7);
            intro.to(coralLight, { intensity: 1.6, duration: 0.8, ease: 'power2.out' }, 3.05);
        }
        intro.to(q('.nav'), { opacity: 1, duration: 0.8 }, 0.4);
        intro.to(q('.eyebrow'), { opacity: 1, duration: 0.8 }, 0.9);
        intro.to(q('.hero-h1 .w'), { opacity: 1, y: 0, duration: 1.0, stagger: 0.09 }, 1.1);
        intro.to(q('.hero-sub'), { opacity: 1, duration: 0.9 }, 1.8);
        intro.to(q('.hero-cta'), { opacity: 1, duration: 0.9 }, 2.0);
        intro.to(q('.scroll-cue'), { opacity: 1, duration: 0.9 }, 2.6);
    }

    return () => {
        cancelAnimationFrame(rafId);
        visObs.disconnect();
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('resize', resize);
        if (intro) intro.kill();
        wall.children.forEach(m => m.material.dispose());
        brickGeo.dispose();
        groundGeo.dispose(); groundMat.dispose();
        dustGeo.dispose(); dustMat.dispose();
        renderer.dispose();
    };
}

export function LandingPage() {
    const history = useHistory();
    const rootRef = useRef();
    const heroRef = useRef();
    const canvasRef = useRef();

    const [status, setStatus] = useState('idle');
    const [errorMsg, setErrorMsg] = useState('');
    const [exiting, setExiting] = useState(false);
    const [formData, setFormData] = useState(EMPTY_FORM);

    const goToLogin = () => {
        setExiting(true);
        setTimeout(() => history.push('/account/login'), 560);
    };

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('sending');
        setErrorMsg('');
        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
            if (!res.ok) throw new Error();
            setStatus('success');
        } catch {
            setStatus('error');
            setErrorMsg('Gửi thất bại. Vui lòng thử lại hoặc liên hệ trực tiếp qua số điện thoại.');
        }
    };

    /* three.js hero wall */
    useEffect(() => {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        try {
            return buildWall(canvasRef.current, heroRef.current, rootRef.current, reduceMotion);
        } catch {
            /* WebGL unavailable — show the page without the 3D scene */
            gsap.set(rootRef.current.querySelectorAll('.nav, .eyebrow, .hero-sub, .hero-cta, .scroll-cue, .hero-h1 .w'), { opacity: 1, y: 0 });
        }
    }, []);

    /* scroll-driven motion */
    useEffect(() => {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const ctx = gsap.context(() => {
            if (reduceMotion) {
                gsap.set('.reveal, .manifesto .mw', { opacity: 1, y: 0 });
                gsap.utils.toArray('.count').forEach(el => {
                    el.textContent = parseInt(el.dataset.to, 10).toLocaleString('vi-VN');
                });
                gsap.set('.jprog-fill', { width: '100%' });
                return;
            }

            gsap.utils.toArray('.reveal').forEach(el => {
                gsap.to(el, {
                    opacity: 1, y: 0, duration: 1.0, ease: 'power3.out',
                    scrollTrigger: { trigger: el, start: 'top 86%' },
                });
            });

            gsap.to('.manifesto .mw', {
                opacity: 1, stagger: 0.06, ease: 'none',
                scrollTrigger: { trigger: '.manifesto', start: 'top 72%', end: 'center 45%', scrub: 0.4 },
            });

            gsap.utils.toArray('.count').forEach(el => {
                const to = parseInt(el.dataset.to, 10);
                const obj = { val: 0 };
                gsap.to(obj, {
                    val: to, duration: 1.8, ease: 'power2.out',
                    onUpdate: () => { el.textContent = Math.round(obj.val).toLocaleString('vi-VN'); },
                    scrollTrigger: { trigger: el, start: 'top 88%' },
                });
            });

            gsap.to('.jprog-fill', {
                width: '100%', ease: 'none',
                scrollTrigger: { trigger: '.steps', start: 'top 70%', end: 'bottom 55%', scrub: 0.5 },
            });
            gsap.utils.toArray('.step').forEach(el => {
                gsap.from(el, {
                    y: 70, opacity: 0, duration: 0.9, ease: 'power3.out',
                    scrollTrigger: { trigger: el, start: 'top 88%' },
                });
                ScrollTrigger.create({
                    trigger: el, start: 'top 62%', end: 'bottom 40%',
                    onToggle: self => el.classList.toggle('active', self.isActive),
                });
            });

            gsap.from('.foot-giant', {
                y: 110, ease: 'none',
                scrollTrigger: { trigger: '.foot', start: 'top bottom', end: 'top 35%', scrub: 0.6 },
            });
        }, rootRef);
        return () => ctx.revert();
    }, []);

    return (
        <div className="landing" ref={rootRef}>
            <div className={`brick-overlay${exiting ? ' active' : ''}`} />

            {/* ===== NAV ===== */}
            <nav className="nav">
                <img src="/logos/MASON_logo-ngang-w.png" alt="The Mason" className="nav-logo" />
                <div className="nav-links">
                    <a href="#pillars">Vì sao chọn</a>
                    <a href="#journey">Hành trình</a>
                    <a href="#enroll">Đăng ký</a>
                </div>
                <button className="nav-login" onClick={goToLogin}>Đăng nhập</button>
            </nav>

            {/* ===== HERO ===== */}
            <section className="hero" ref={heroRef}>
                <canvas className="wall-canvas" ref={canvasRef} />
                <div className="hero-wash" />
                <div className="hero-inner">
                    <div className="eyebrow">English Center · Hạ Long · từ 2019</div>
                    <h1 className="hero-h1">
                        <span className="w">Found</span>{' '}
                        <span className="w">Your</span>{' '}
                        <span className="w"><em>Stone.</em></span>
                    </h1>
                    <p className="hero-sub">
                        The Mason là trung tâm tiếng Anh dạy theo lối của người thợ xây —
                        nền móng vững, từng viên gạch một, và một người cố vấn đồng hành ở mỗi bậc thang.
                    </p>
                    <div className="hero-cta">
                        <a href="#enroll" className="btn-coral">Đăng ký tư vấn miễn phí</a>
                        <button className="btn-ghost" onClick={goToLogin}>Đăng nhập học viên</button>
                    </div>
                </div>
                <div className="scroll-cue"><span>Cuộn để khám phá</span><span className="bar" /></div>
            </section>

            {/* ===== MARQUEE ===== */}
            <div className="marquee" aria-hidden="true">
                <div className="marquee-track">
                    {[0, 1, 2, 3].map(i => <MarqueeUnit key={i} />)}
                </div>
            </div>

            {/* ===== MANIFESTO ===== */}
            <section className="manifesto">
                <p>
                    {MANIFESTO.map((w, i) => (
                        <React.Fragment key={i}>
                            <span className={w.accent ? 'mw accent' : 'mw'}>{w.t}</span>{' '}
                        </React.Fragment>
                    ))}
                </p>
            </section>

            {/* ===== PILLARS BENTO ===== */}
            <section className="pillars" id="pillars">
                <div className="wrap">
                    <div className="pillars-head">
                        <div className="reveal">
                            <div className="kicker">Vì sao chọn The Mason</div>
                            <h2 className="h2">Ba điều <em>không đổi</em><br />trong mỗi khóa.</h2>
                        </div>
                        <p className="reveal">Sau bảy năm, chúng tôi chỉ giữ lại những gì thực sự tạo ra kết quả.</p>
                    </div>
                    <div className="bento">
                        <div className="card card-a reveal">
                            <div className="card-img" style={{ backgroundImage: "url('/IMG_8842.JPG')" }} />
                            <div className="card-grad" />
                            <div className="card-num">01</div>
                            <span className="card-tag tag-coral">Sĩ số giới hạn</span>
                            <h3>Lớp nhỏ, tối đa 8 người</h3>
                            <p>Đủ chỗ cho mọi người nói, đủ thời gian cho giáo viên nghe. Không ai bị bỏ lại phía sau bức tường.</p>
                        </div>
                        <div className="card card-b reveal">
                            <div className="card-num">02</div>
                            <span className="card-tag tag-sky">Đồng hành 1:1</span>
                            <h3>Cố vấn học tập riêng</h3>
                            <p>Một giáo viên đồng hành, theo dõi tiến độ của bạn hàng tuần.</p>
                        </div>
                        <div className="card card-c reveal">
                            <div className="card-num">03</div>
                            <span className="card-tag tag-mint">Bảo chứng kết quả</span>
                            <h3>Cam kết đầu ra</h3>
                            <p>Không đạt mục tiêu của khóa? Bạn được học lại miễn phí.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== NUMBERS BAND ===== */}
            <section className="numbers">
                <div className="wrap">
                    {NUMBERS.map((n, i) => (
                        <div className="num-cell reveal" key={i}>
                            <div className="num-val"><span className="count" data-to={n.to}>0</span><span className="unit">{n.unit}</span></div>
                            <div className="num-lbl">{n.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ===== JOURNEY ===== */}
            <section className="journey" id="journey">
                <div className="wrap journey-grid">
                    <div className="journey-left">
                        <div className="kicker">Hành trình</div>
                        <h2 className="h2">Xây như <em>thợ cả.</em></h2>
                        <p>Mỗi khóa học là một bức tường. Chúng tôi xây nó theo đúng trình tự của người thợ — không bỏ bước, không xây vội.</p>
                        <div className="journey-progress"><div className="jprog-fill" /></div>
                    </div>
                    <div className="steps">
                        {STEPS.map(stp => (
                            <div className={stp.last ? 'step step-last' : 'step'} key={stp.no}>
                                <div className="step-meta"><span className="step-no">{stp.no}</span><h3>{stp.title}</h3></div>
                                <p>{stp.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== ENROLL ===== */}
            <section className="enroll" id="enroll">
                <div className="wrap enroll-grid">
                    <div className="enroll-left reveal">
                        <div className="kicker">Đăng ký tư vấn</div>
                        <h2 className="h2">Để lại thông tin,<br />chúng tôi <em>gọi lại.</em></h2>
                        <p className="lead">Một buổi tư vấn 1:1 miễn phí — kèm lộ trình học cá nhân hóa cho bạn.</p>
                        <div className="trust">
                            <div><span className="dot" />Phản hồi trong 24 giờ làm việc</div>
                            <div><span className="dot" />Tư vấn hoàn toàn miễn phí, không ràng buộc</div>
                            <div><span className="dot" />Lộ trình học cá nhân hóa theo năng lực</div>
                        </div>
                    </div>
                    <div className="form-card reveal">
                        {status === 'success' ? (
                            <div className="success">
                                <div className="ring">
                                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </div>
                                <h3>Cảm ơn bạn đã liên hệ!</h3>
                                <p>Đội ngũ The Mason đã nhận được thông tin của bạn.<br />
                                Chúng tôi sẽ gọi điện trong vòng <strong>24 giờ tới.</strong></p>
                                <button className="success-login" type="button" onClick={goToLogin}>
                                    Hoặc đăng nhập nếu bạn đã là học viên →
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                <div className="form-head">
                                    <img src="/logos/MASON_logo-white.png" alt="" />
                                    <div>
                                        <h3>Tư vấn miễn phí</h3>
                                        <p>Phản hồi trong vòng 24 giờ làm việc</p>
                                    </div>
                                </div>
                                <div className="frow">
                                    <div className="field">
                                        <label>Họ và tên <span className="req">*</span></label>
                                        <input type="text" name="from_name" required placeholder="VD: Nguyễn Việt Anh" value={formData.from_name} onChange={handleChange} />
                                    </div>
                                    <div className="field">
                                        <label>Số điện thoại <span className="req">*</span></label>
                                        <input type="tel" name="phone" required placeholder="VD: 0901 234 567" value={formData.phone} onChange={handleChange} />
                                    </div>
                                </div>
                                <div className="frow full">
                                    <div className="field">
                                        <label>Email <span className="req">*</span></label>
                                        <input type="email" name="from_email" required placeholder="ten@email.com" value={formData.from_email} onChange={handleChange} />
                                    </div>
                                </div>
                                <div className="frow">
                                    <div className="field">
                                        <label>Trình độ hiện tại</label>
                                        <select name="level" value={formData.level} onChange={handleChange}>
                                            <option value="">Chọn trình độ</option>
                                            <option>Mới bắt đầu</option>
                                            <option>Sơ cấp (A1–A2)</option>
                                            <option>Trung cấp (B1–B2)</option>
                                            <option>Cao cấp (C1–C2)</option>
                                            <option>Chưa rõ — cần đánh giá</option>
                                        </select>
                                    </div>
                                    <div className="field">
                                        <label>Khóa học quan tâm</label>
                                        <select name="course" value={formData.course} onChange={handleChange}>
                                            <option value="">Chọn khóa</option>
                                            <option>IELTS Foundation</option>
                                            <option>IELTS Mastery</option>
                                            <option>Giao tiếp hằng ngày</option>
                                            <option>Business English</option>
                                            <option>Cambridge Young Learners</option>
                                            <option>Phát âm chuyên sâu</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="frow full">
                                    <div className="field">
                                        <label>Mục tiêu cụ thể <span className="opt">(không bắt buộc)</span></label>
                                        <textarea name="message" placeholder="VD: Muốn đạt IELTS 6.5 trước tháng 9 để nộp hồ sơ du học." value={formData.message} onChange={handleChange} />
                                    </div>
                                </div>
                                {/* Honeypot: hidden from humans, bots fill it — server silently discards the submission */}
                                <div className="hp" aria-hidden="true">
                                    <input type="text" name="website" value={formData.website} onChange={handleChange} tabIndex={-1} autoComplete="off" />
                                </div>
                                <label className="consent">
                                    <input type="checkbox" required defaultChecked />
                                    <span>Tôi đồng ý cho The Mason liên hệ qua số điện thoại và email đã cung cấp.</span>
                                </label>
                                {status === 'error' && <p className="form-error">{errorMsg}</p>}
                                <button className="btn-coral submit" type="submit" disabled={status === 'sending'}>
                                    {status === 'sending' ? 'Đang gửi...' : 'Nhận tư vấn miễn phí'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            {/* ===== FOOTER ===== */}
            <footer className="foot">
                <div className="foot-giant">Found Your Stone.</div>
                <div className="wrap">
                    <div className="foot-grid">
                        <div>
                            <img src="/logos/MASON_logo-ngang-w.png" alt="The Mason" className="foot-logo" />
                            <p>Trung tâm Anh ngữ The Mason — Hạ Long, Quảng Ninh. Xây tiếng Anh như người thợ xây: chắc từng viên gạch, vững cả bức tường.</p>
                        </div>
                        <div className="foot-col">
                            <h4>Khám phá</h4>
                            <a href="#pillars">Vì sao chọn The Mason</a>
                            <a href="#journey">Hành trình học</a>
                            <a href="#enroll">Đăng ký tư vấn</a>
                        </div>
                        <div className="foot-col">
                            <h4>Học viên</h4>
                            <button className="foot-link-btn" onClick={goToLogin}>Đăng nhập</button>
                            <a href="#enroll">Tư vấn miễn phí</a>
                        </div>
                        <div className="foot-col">
                            <h4>Liên hệ</h4>
                            <p className="foot-info">{ADDRESS}</p>
                            <a href="tel:+84888070619">{PHONE}</a>
                            <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
                        </div>
                    </div>
                    <div className="foot-base">
                        <span>© 2026 The Mason English Center</span>
                        <span>Hạ Long · Quảng Ninh · Việt Nam</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
